/**
 * Backend Logger Service
 * Sends client-side logs to the backend for centralized logging
 */

interface LogEntry {
  level: 'error' | 'warn' | 'info' | 'debug';
  message: string;
  error?: {
    message?: string;
    stack?: string;
    name?: string;
    [key: string]: any;
  };
  context?: {
    [key: string]: any;
  };
  timestamp?: string;
}

class BackendLogger {
  private apiUrl: string;
  private enabled: boolean = true;
  private queue: LogEntry[] = [];
  private isSending: boolean = false;

  constructor() {
    // Determine API URL based on environment
    if (import.meta.env.PROD) {
      this.apiUrl = 'https://chat.eir.space/api/logging';
    } else {
      this.apiUrl = import.meta.env.REACT_APP_API_URL 
        ? `${import.meta.env.REACT_APP_API_URL}/api/logging`
        : 'http://localhost:3001/api/logging';
    }
  }

  /**
   * Send log entry to backend
   */
  private async sendLog(entry: LogEntry): Promise<void> {
    if (!this.enabled) {
      return;
    }

    try {
      const payload = {
        ...entry,
        timestamp: entry.timestamp || new Date().toISOString(),
        userAgent: navigator.userAgent,
        url: window.location.href,
      };

      const response = await fetch(`${this.apiUrl}/client-error`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        console.warn('[BackendLogger] Failed to send log:', response.status);
      }
    } catch (error) {
      // Silently fail - don't want logging to break the app
      console.warn('[BackendLogger] Error sending log to backend:', error);
    }
  }

  /**
   * Process queued logs
   */
  private async processQueue(): Promise<void> {
    if (this.isSending || this.queue.length === 0) {
      return;
    }

    this.isSending = true;
    const entries = [...this.queue];
    this.queue = [];

    for (const entry of entries) {
      await this.sendLog(entry);
      // Small delay between sends to avoid overwhelming the server
      await new Promise(resolve => setTimeout(resolve, 100));
    }

    this.isSending = false;

    // Process any new entries that were added while sending
    if (this.queue.length > 0) {
      setTimeout(() => this.processQueue(), 1000);
    }
  }

  /**
   * Log an error
   */
  error(message: string, error?: Error | any, context?: Record<string, any>): void {
    const entry: LogEntry = {
      level: 'error',
      message,
      error: error instanceof Error
        ? {
            message: error.message,
            stack: error.stack,
            name: error.name,
          }
        : error,
      context,
    };

    // Also log to console
    console.error(`[BackendLogger] ${message}`, error, context);

    // Add to queue
    this.queue.push(entry);
    this.processQueue();
  }

  /**
   * Log a warning
   */
  warn(message: string, context?: Record<string, any>): void {
    const entry: LogEntry = {
      level: 'warn',
      message,
      context,
    };

    console.warn(`[BackendLogger] ${message}`, context);
    this.queue.push(entry);
    this.processQueue();
  }

  /**
   * Log info
   */
  info(message: string, context?: Record<string, any>): void {
    const entry: LogEntry = {
      level: 'info',
      message,
      context,
    };

    console.log(`[BackendLogger] ${message}`, context);
    this.queue.push(entry);
    this.processQueue();
  }

  /**
   * Enable/disable logging
   */
  setEnabled(enabled: boolean): void {
    this.enabled = enabled;
  }
}

// Export singleton instance
export const backendLogger = new BackendLogger();
